import { waitUntil } from '@/helpers/waitUntil';
import { store } from '@/store';
import { recorderFmc } from '@/tauri/controller/recording';
import { type ResolvedStartDeviceNames } from '@/tauri/resolveDeviceNamesForStart';
import { bridgeErrorKind } from '@heidi/plugin-core';
import { createModel } from '@rematch/core';
import { cloneDeep } from 'lodash-es';
import { type RootModel } from '.';
import { type RecordingError } from './tauriRecordErrors';
// import { RecordingStatus } from '../scribeModel';
import { logger } from '@/utils/logger/logger';
// todo use tanstack's states

export enum UploadStatus {
  Queued = 'queued',
  Uploading = 'uploading',
  Completed = 'completed',
  Failed = 'failed',
}
export { RecordingError } from './tauriRecordErrors';

export type CaptureSilenceSource = 'mic' | 'speaker';

/**
 * A pending silent-capture trigger for one source, set by the ipc.ts
 * `AUDIO_CAPTURE_ACTIVITY` listener and consumed (one-shot, like
 * `recordingError`) by `CaptureSilenceRecovery`, which owns all policy.
 */
export type CaptureSilenceEntry = {
  /** Seconds of continuous silence when the monitor fired. */
  silenceSecs: number;
  /** Whether the source ever produced energetic audio this producer lifetime. */
  seenEnergetic: boolean;
  /** Producer-spawn id used for stale-event filtering. */
  captureInstanceId: number;
};

export type CaptureSilenceWarningVariant =
  /** Speaker silent during a matched telehealth call. One-click "Capture {app} audio". */
  | 'speaker_capture_app'
  /** Mic never energetic and a comms-default mic differs from the one in use. "Use {mic}". */
  | 'mic_use_comms_default'
  /**
   * Mic never energetic, no better mic to suggest, hard stop armed. Names the in-use mic. Heidi stops
   * only when NO source captures audio, so the copy must not promise a stop on the mic latch alone.
   * Separate from `mic_informational`: a source with no samples is a device fault, not a quiet room.
   */
  | 'mic_never_energetic'
  /**
   * Mic silent with no one-click fix to offer: never energetic with no better
   * mic known, or post-speech silence (the mic WAS working, so a mic switch
   * is not the remedy). Informational only.
   */
  | 'mic_informational';

/**
 * A policy-approved warning that is currently visible. It drives the popover and the red device-pill
 * until the source reports `active` or the recording ends. Separate from `captureSilence` (the raw
 * trigger) because most triggers produce no warning.
 */
export type CaptureSilenceWarning = {
  source: CaptureSilenceSource;
  variant: CaptureSilenceWarningVariant;
  /**
   * Matched telehealth app name (speaker), suggested mic label (mic
   * actionable), or the in-use mic label (`mic_never_energetic`).
   */
  targetLabel?: string;
};

/**
 * A chunk cadence together with the recording session it was locked for. See
 * {@link InitialState.lockedChunkInterval}.
 */
export type LockedChunkInterval = {
  sessionId: string;
  chunkIntervalSecs: number;
};

/** The most backup blobs held at once. Each is a full MP3 in memory, so the oldest is freed first. */
export const MAX_RECORDING_BACKUPS = 3;

/** One session's backup: the extracted MP3 while it is the retry copy, and whether its upload landed. */
export type RecordingBackup = {
  blob: Blob | null;
  /** The file the blob was read from, so a success can be matched to the blob it covers. */
  path: string | null;
  uploaded: boolean;
  /** Position in storing order, so the oldest can be evicted past the cap. */
  storedOrder: number;
};

/**
 * Frees the oldest blobs past the cap, never the one just stored. An entry keeps its `uploaded` flag,
 * which a running wait may still read, and a settled entry holds no blob so it takes no slot.
 */
function evictOldestBlobs(
  backups: Record<string, RecordingBackup>,
  keepSessionId: string
): Record<string, RecordingBackup> {
  const holders = Object.keys(backups).filter((sessionId) => backups[sessionId]?.blob);
  if (holders.length <= MAX_RECORDING_BACKUPS) {
    return backups;
  }
  const evicted = holders
    .filter((sessionId) => sessionId !== keepSessionId)
    .sort((a, b) => (backups[a]?.storedOrder ?? 0) - (backups[b]?.storedOrder ?? 0))
    .slice(0, holders.length - MAX_RECORDING_BACKUPS);
  const kept = { ...backups };
  evicted.forEach((sessionId) => {
    const backup = kept[sessionId];
    if (backup) {
      kept[sessionId] = { ...backup, blob: null, path: null };
    }
  });
  return kept;
}

/** A new chunk state starts a new wait, so an earlier backup's uploaded flag must not release it. */
function withUploadedFlagCleared(
  backups: Record<string, RecordingBackup>,
  sessionId: string | null
): Record<string, RecordingBackup> {
  const backup = sessionId ? backups[sessionId] : undefined;
  if (!sessionId || !backup?.uploaded) {
    return backups;
  }
  return { ...backups, [sessionId]: { ...backup, uploaded: false } };
}

export type InitialState = {
  chunks: {
    current_index: number;
    blobs: {
      name: string;
      duration: number;
      size: number;
      chunk: Blob[];
      status: UploadStatus;
    }[];
    recording_id?: string;
    session_id: string | null;
  };
  /** Keyed by session. It outlives `chunks`, which `setChunks` replaces on every start and chunk. */
  backups: Record<string, RecordingBackup>;
  /** Counts backups stored, giving each a `storedOrder` without reading a clock in a reducer. */
  backupSequence: number;
  /**
   * Whether this recording uses the v2 EKS transcription service for chunk upload. Locked at session
   * start so a mid-session flag flip cannot corrupt uploads; the upload mutation reads this, not the flag.
   * Top level, not inside `chunks`, because `setChunks` replaces `chunks` and would wipe it.
   */
  useTranscriptionServiceV2?: boolean;
  /**
   * Whether Rust owns this recording's chunk uploads (the `online_chunk_upload` pipeline) rather than
   * the FE upload path. Locked at session start so `useMutateStopRecording` takes the matching stop
   * path despite a mid-session flag flip. Top level so `setChunks` does not wipe it.
   */
  useRustChunkUpload?: boolean;
  /**
   * Cadence (seconds) the Rust upload session started with, stamped with its session because a later start can
   * overwrite this slot during an earlier stop. Readers must compare {@link LockedChunkInterval.sessionId} and
   * skip the truncation heuristic on a mismatch or `undefined` (cleared on every start, unset if Rust failed).
   */
  lockedChunkInterval?: LockedChunkInterval;
  /**
   * Whether proactive low-disk monitoring was enabled when this logical
   * capture started. Device-change restarts reuse this locked decision rather
   * than re-reading a rollout flag partway through the same session.
   */
  lowDiskMonitorEnabled?: boolean;
  /**
   * The exact device names passed to `start_recording` plus the speaker capture mode they imply
   * (from `resolveDeviceNamesForStart`). Set on every start so a prior value never leaks; top level so
   * `setChunks` does not wipe it. Read by analytics enrichment and the silent-capture warning layer.
   */
  lockedStartDevices?: ResolvedStartDeviceNames;
  getCurrentRecordingError?: RecordingError;
  recordingError?: RecordingError;
  interval?: ReturnType<typeof setInterval>;
  /** Pending silent-capture triggers, one-shot per source. */
  captureSilence: Partial<Record<CaptureSilenceSource, CaptureSilenceEntry>>;
  /** Policy-approved warnings driving the popover + red pill treatment. */
  captureSilenceWarnings: Partial<Record<CaptureSilenceSource, CaptureSilenceWarning>>;
  /** Sources already warned this recording (analytics/notification cap). */
  captureSilenceWarnedSources: Partial<Record<CaptureSilenceSource, boolean>>;
  /**
   * Per-source `capture_instance_id` high-water marks. The ipc listener
   * discards events below the mark (stale monitor); reset per recording start
   * so a Rust-side counter restart can't strand the FE.
   */
  captureActivityHighWater: Record<CaptureSilenceSource, number>;
  /**
   * Epoch-ms fence set by every `resetCaptureSilence`; the ipc listener drops events whose Rust `ts`
   * predates it. Covers a source that never emitted this recording, whose id mark is stale.
   */
  captureActivityAcceptedAfterTs: number;
  /**
   * Per-source clear generation, bumped by every clear/reset. An async policy cycle uses it to detect
   * a clear (recovery/stop/restart) during its device refresh, so it shows no warning for a healthy source.
   */
  captureSilenceClearGen: Record<CaptureSilenceSource, number>;
  /**
   * Per-source latch: no energetic audio yet this recording. Set by a `silent` event with `seenEnergetic: false`,
   * cleared by `active` recovery and every reset. It outlives the one-shot `captureSilence` so the hard-stop
   * timer and stop mutation can read it. A source that never emitted stays `false` (no evidence is not silence).
   */
  captureNeverEnergetic: Record<CaptureSilenceSource, boolean>;
};

const initialState: InitialState = {
  chunks: {
    current_index: 0,
    blobs: [],
    session_id: null,
  },
  backups: {},
  backupSequence: 0,
  captureSilence: {},
  captureSilenceWarnings: {},
  captureSilenceWarnedSources: {},
  captureActivityHighWater: { mic: 0, speaker: 0 },
  captureActivityAcceptedAfterTs: 0,
  captureSilenceClearGen: { mic: 0, speaker: 0 },
  captureNeverEnergetic: { mic: false, speaker: false },
};

export const tauriRecordModel = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setChunks(state, payload: InitialState['chunks']) {
      return {
        ...state,
        chunks: payload,
        backups: withUploadedFlagCleared(state.backups, payload.session_id),
      };
    },
    setInterval(state, payload: InitialState['interval']) {
      return { ...state, interval: payload };
    },
    /**
     * Lock the v2 boolean for this recording. Set once at session start by `useMutateStartTauriRecording`,
     * read on every chunk by `useMutateUploadChunkRecording`.
     */
    setUseTranscriptionServiceV2(state, payload: boolean) {
      return { ...state, useTranscriptionServiceV2: payload };
    },
    /**
     * Lock whether Rust owns chunk upload for this recording. Set at session start (true and false, so a
     * prior session's value never leaks) and read at stop by `useMutateStopRecording`.
     */
    setUseRustChunkUpload(state, payload: boolean) {
      return { ...state, useRustChunkUpload: payload };
    },
    /**
     * Lock the chunk cadence Rust runs for this recording, stamped with its session so a stop can tell its own
     * cadence from one a concurrent start wrote. Set to `undefined` on every start and to the real value only
     * after `startSession` resolves, so a thrown start locks nothing.
     */
    setLockedChunkInterval(state, payload?: LockedChunkInterval) {
      return { ...state, lockedChunkInterval: payload };
    },
    /** Lock proactive low-disk monitoring for this logical capture. */
    setLowDiskMonitorEnabled(state, payload: boolean) {
      return { ...state, lowDiskMonitorEnabled: payload };
    },
    /**
     * Lock the resolved start-time device names + speaker capture mode for
     * this recording. Set by both start sites (the start hook and the
     * legacy device-change restart).
     */
    setLockedStartDevices(state, payload: ResolvedStartDeviceNames) {
      return { ...state, lockedStartDevices: payload };
    },
    editChunkStatus(
      state,
      payload: { sessionId: string; index: number; status: UploadStatus }
    ) {
      if (state.chunks.session_id !== payload.sessionId) {
        return state;
      }
      const chunks = cloneDeep(state.chunks);
      const blobEdited = chunks.blobs[payload.index];
      if (blobEdited) {
        blobEdited.status = payload.status;
      }
      return { ...state, chunks };
    },
    setRecordingError(state, payload?: RecordingError) {
      return { ...state, recordingError: payload };
    },

    setCurrentRecordingError(state, payload?: RecordingError) {
      return { ...state, getCurrentRecordingError: payload };
    },
    setBackupFile(state, payload: { sessionId: string; blob: Blob; path: string }) {
      const backupSequence = state.backupSequence + 1;
      const backup: RecordingBackup = {
        blob: payload.blob,
        path: payload.path,
        uploaded: false,
        storedOrder: backupSequence,
      };
      const backups = { ...state.backups, [payload.sessionId]: backup };
      return {
        ...state,
        backups: evictOldestBlobs(backups, payload.sessionId),
        backupSequence,
      };
    },
    /**
     * Marks the backup uploaded and frees its blob and path in one step, only when the upload that
     * landed read from the stored file. The uploaded flag stays after the blob is gone.
     */
    settleBackupUploaded(state, payload: { sessionId: string; path: string }) {
      const backup = state.backups[payload.sessionId];
      if (!backup || backup.path !== payload.path) {
        return state;
      }
      return {
        ...state,
        backups: {
          ...state.backups,
          [payload.sessionId]: { ...backup, blob: null, path: null, uploaded: true },
        },
      };
    },

    /**
     * Record a `silent` monitor event: set the one-shot trigger and advance
     * the per-source high-water mark so stale torn-down-monitor events drop.
     */
    setCaptureSilence(
      state,
      payload: { source: CaptureSilenceSource; entry: CaptureSilenceEntry }
    ) {
      return {
        ...state,
        captureSilence: { ...state.captureSilence, [payload.source]: payload.entry },
        captureActivityHighWater: {
          ...state.captureActivityHighWater,
          [payload.source]: Math.max(
            state.captureActivityHighWater[payload.source],
            payload.entry.captureInstanceId
          ),
        },
        // A post-speech alert (`seenEnergetic: true`) proves the source DID
        // work, so it can only ever clear the latch, never set it.
        captureNeverEnergetic: {
          ...state.captureNeverEnergetic,
          [payload.source]: !payload.entry.seenEnergetic,
        },
      };
    },

    /**
     * Consume a pending trigger without touching the visible warning, as `AudioDeviceLostRecovery` consumes
     * `recordingError`, so a re-render cannot re-fire the policy.
     */
    consumeCaptureSilence(state, payload: CaptureSilenceSource) {
      const remaining = { ...state.captureSilence };
      delete remaining[payload];
      return { ...state, captureSilence: remaining };
    },

    /**
     * An `active` recovery event: drop the pending trigger and visible warning for the source, advance the
     * high-water mark, and reset the source's `captureNeverEnergetic` answer.
     */
    clearCaptureSilence(
      state,
      payload: { source: CaptureSilenceSource; captureInstanceId?: number }
    ) {
      const remainingTriggers = { ...state.captureSilence };
      delete remainingTriggers[payload.source];
      const remainingWarnings = { ...state.captureSilenceWarnings };
      delete remainingWarnings[payload.source];
      return {
        ...state,
        captureSilence: remainingTriggers,
        captureSilenceWarnings: remainingWarnings,
        captureActivityHighWater: {
          ...state.captureActivityHighWater,
          [payload.source]: Math.max(
            state.captureActivityHighWater[payload.source],
            payload.captureInstanceId ?? 0
          ),
        },
        captureSilenceClearGen: {
          ...state.captureSilenceClearGen,
          [payload.source]: state.captureSilenceClearGen[payload.source] + 1,
        },
        captureNeverEnergetic: {
          ...state.captureNeverEnergetic,
          [payload.source]: false,
        },
      };
    },

    /** Surface a policy-approved warning (popover + red pill treatment). */
    setCaptureSilenceWarning(state, payload: CaptureSilenceWarning) {
      return {
        ...state,
        captureSilenceWarnings: {
          ...state.captureSilenceWarnings,
          [payload.source]: payload,
        },
      };
    },

    /** Remember this source already warned this recording. */
    markCaptureSilenceWarned(state, payload: CaptureSilenceSource) {
      return {
        ...state,
        captureSilenceWarnedSources: {
          ...state.captureSilenceWarnedSources,
          [payload]: true,
        },
      };
    },

    /**
     * Clear + dismiss on start/stop/restart; always drops triggers and warnings. A mid-recording restart keeps the
     * once-per-recording warned marks and bumps high-water marks past the torn-down producers' ids (the listener
     * drops `id < mark`). A genuine start/stop wipes everything so a Rust counter restart cannot strand the FE.
     */
    resetCaptureSilence(state, payload?: { isMidRecordingRestart?: boolean }) {
      return {
        ...state,
        captureSilence: {},
        captureSilenceWarnings: {},
        captureSilenceWarnedSources: payload?.isMidRecordingRestart
          ? state.captureSilenceWarnedSources
          : {},
        captureActivityHighWater: payload?.isMidRecordingRestart
          ? {
              mic: state.captureActivityHighWater.mic + 1,
              speaker: state.captureActivityHighWater.speaker + 1,
            }
          : { mic: 0, speaker: 0 },
        // Every reset dispatches after the old monitors are torn down, so
        // rejecting older emission timestamps fences even a source whose
        // instance id the FE never learned (it never emitted this recording).
        captureActivityAcceptedAfterTs: Date.now(),
        captureSilenceClearGen: {
          mic: state.captureSilenceClearGen.mic + 1,
          speaker: state.captureSilenceClearGen.speaker + 1,
        },
        // Wiped on a mid-recording restart too: respawned monitors re-alert at 20 s if
        // the new source is also dead, so a stale latch could only mislabel a working
        // replacement device.
        captureNeverEnergetic: { mic: false, speaker: false },
      };
    },

    clear() {
      return initialState;
    },
  },
  effects: (dispatch) => ({
    async get_backup_file(options: { deleteAfterRead?: boolean } = {}) {
      const recording = await recorderFmc.get_backup_file();
      if (!recording) {
        return;
      }
      const bytes = await recorderFmc.get_file_from_path(recording, options);
      const byteArray = new Uint8Array(bytes.bytes);
      const blob = new Blob([byteArray], { type: 'audio/mp3' });
      return { blob, path: recording };
    },

    async stopTauriRecording() {
      try {
        if (!store.getState().timerModel.isRunning) {
          await waitUntil(() => {
            return store.getState().timerModel.isRunning;
          }, 2000);
        }
        dispatch.timerModel.onPause();
        await recorderFmc.stop();

        // Do not emit recording-stopped here: the idle stream restart must wait for the
        // cleanup in `useMutateStopRecording` (it calls `emitStopped()`). Emitting early
        // races the restart against operations still using audio resources.
        logger.debug('[Recording] recorderFmc.stop() completed');
      } catch (error) {
        logger.error('error stopping tauri recording', error);
        throw error;
      } finally {
        // The 60s upload interval must die even when the recorder stop throws.
        const interval = store.getState().tauriRecordModel.interval;
        if (interval) {
          clearInterval(interval);
        }
      }
    },

    async getFilesForSession(sessionId: string) {
      // Fired and forgotten after a stop, so a failure is logged here and not left as an unhandled rejection.
      try {
        const files = await recorderFmc.get_files_for_session(sessionId);
        dispatch.scribeRecordModel.setTauriSessionFiles({
          sessionId: sessionId,
          files: files,
        });
      } catch (error) {
        logger.warn('[Recording] could not read the session files', {
          errorKind: bridgeErrorKind(error),
        });
      }
    },
  }),
});
